import type { IDailyStats, TAttributionModel, TConversionStats } from '@feathr/blackbox';

/**
 * A daily-stats row as consumed by the table: the blackbox `IDailyStats` shape plus the
 * runtime `conversions` map. `IDailyStats` does not declare `conversions`, but the stats
 * API returns it, so we add it explicitly rather than relying on a loose index signature.
 */
export type TDailyStatsRow = IDailyStats & { conversions?: TConversionStats };

export interface IDailyStatsTotals {
  clicks: number;
  conversions: number;
  spend: number;
  views: number;
}

/** The dynamic column id for the conversions column of a given attribution model. */
export function getConversionsColumnId(attributionModel: TAttributionModel): string {
  return `conversions__${attributionModel}__num`;
}

/** Conversion count for the given attribution model (0 when absent). */
export function getConversions(row: TDailyStatsRow, attributionModel: TAttributionModel): number {
  return row.conversions?.[attributionModel]?.num ?? 0;
}

/** Click-through rate as a fraction — NumberCell's `percent` format expects a fraction. */
export function getCtr(row: TDailyStatsRow): number {
  const views = row.num_views ?? 0;
  return views > 0 ? (row.num_clicks ?? 0) / views : 0;
}

/** Cost per mille (per thousand views). */
export function getCpm(row: TDailyStatsRow): number {
  const views = row.num_views ?? 0;
  return views > 0 ? ((row.spend ?? 0) / views) * 1000 : 0;
}

/** Cost per click. */
export function getCpc(row: TDailyStatsRow): number {
  const clicks = row.num_clicks ?? 0;
  return clicks > 0 ? (row.spend ?? 0) / clicks : 0;
}

/**
 * Cost per acquisition. Always divides by the `full` attribution model's conversion
 * count regardless of the selected model — carried forward from the pre-migration
 * behavior (see the ticket's dev notes).
 */
export function getCpa(row: TDailyStatsRow): number {
  const conversions = row.conversions?.full?.num ?? 0;
  return conversions > 0 ? (row.spend ?? 0) / conversions : 0;
}

/**
 * Grand totals across every row (not just the current page) for the footer row. The
 * legacy table's footer summed the full dataset, so the totals must be computed outside
 * the table — the table only ever receives the current page's slice.
 */
export function computeTotals(
  rows: TDailyStatsRow[],
  attributionModel: TAttributionModel,
): IDailyStatsTotals {
  return rows.reduce<IDailyStatsTotals>(
    (acc, row) => ({
      clicks: acc.clicks + (row.num_clicks ?? 0),
      conversions: acc.conversions + getConversions(row, attributionModel),
      spend: acc.spend + (row.spend ?? 0),
      views: acc.views + (row.num_views ?? 0),
    }),
    { clicks: 0, conversions: 0, spend: 0, views: 0 },
  );
}

function getSortValue(
  row: TDailyStatsRow,
  columnId: string,
  attributionModel: TAttributionModel,
): number | string {
  if (columnId === 'date') {
    return row.metadata?.date ?? '';
  }
  if (columnId === 'views') {
    return row.num_views ?? 0;
  }
  if (columnId === 'clicks') {
    return row.num_clicks ?? 0;
  }
  if (columnId === 'num_users_active') {
    return row.num_users_active ?? 0;
  }
  if (columnId === 'spend') {
    return row.spend ?? 0;
  }
  if (columnId === getConversionsColumnId(attributionModel)) {
    return getConversions(row, attributionModel);
  }
  return 0;
}

/**
 * Compare two resolved sort values the way react-table v6's `defaultSortMethod` did:
 * numeric columns numerically, otherwise by string. Returns -1/0/1 (direction applied by
 * the caller).
 */
function compareSortValues(av: number | string, bv: number | string): number {
  if (typeof av === 'number' && typeof bv === 'number') {
    if (av === bv) {
      return 0;
    }
    return av < bv ? -1 : 1;
  }
  const as = String(av);
  const bs = String(bv);
  if (as === bs) {
    return 0;
  }
  return as < bs ? -1 : 1;
}

/**
 * Sort rows in memory, mirroring react-table v6's `defaultSortMethod` and multi-sort so
 * the migrated table preserves the pre-migration order. Sort keys apply in priority order
 * (a shift-click adds a secondary key): the first key that distinguishes two rows wins,
 * and fully-tied rows keep their original order (`Array.prototype.sort` is stable).
 * Numeric columns compare numerically, the date column by its ISO string (chronological),
 * and a missing numeric value sorts as 0. Sorting client-side rather than refetching
 * preserves the no-extra-requests behavior established in #7226.
 */
export function sortRows(
  rows: TDailyStatsRow[],
  sort: Array<{ id: string; desc?: boolean }>,
  attributionModel: TAttributionModel,
): TDailyStatsRow[] {
  if (sort.length === 0) {
    return rows;
  }
  return [...rows].sort((a, b) => {
    for (const { id, desc = false } of sort) {
      const cmp = compareSortValues(
        getSortValue(a, id, attributionModel),
        getSortValue(b, id, attributionModel),
      );
      if (cmp !== 0) {
        return desc ? -cmp : cmp;
      }
    }
    return 0;
  });
}
